/**
 * AdminFooter · v2.7.65+E121
 * ═════════════════════════════════════════════════════════════════════
 * Compliance footer + pre-shift "Test Alarm" row.
 *
 *   Row 1  →  Sound-preview strip (5 buttons, one per critical trigger).
 *             Dispatchers hit these BEFORE a shift to confirm their
 *             speakers work end-to-end. Each button dispatches the same
 *             window CustomEvent that the production alert path uses so
 *             the audio engine plays the exact same file.
 *   Row 2  →  Copyright line (centered).
 *   Row 3  →  Ownership line (centered · flush against Row 2, zero gap).
 *
 * Copy for Rows 2/3 is EXACT per Phase-2 spec — do not paraphrase.
 */
import { useCallback, useEffect, useState } from "react";
import { Volume2, VolumeX, Volume1 } from "lucide-react";

const TESTS = [
  { key: "public_booking",  label: "Online Booking",      event: "rtg:public_booking",  ackEvent: "rtg:public_booking_acknowledged", loop: true },
  { key: "trip_initiated",  label: "Ride Started",        event: "rtg:trip_initiated",  loop: false },
  { key: "trip_completed",  label: "Ride Finished",       event: "rtg:trip_completed",  loop: false },
  { key: "chat_message",    label: "Customer Chat",       event: "rtg:chat_message",    loop: false },
  { key: "ticket_pipeline", label: "Ticket Pipeline",     event: "rtg:ticket_pipeline", loop: false },
];

const MUTE_KEY           = "rtg_admin_audio_muted";
const VOLUME_KEY         = "rtg_admin_audio_volume";
const BOOKING_VOLUME_KEY = "rtg_admin_audio_booking_volume";
const BOOKING_RIDER_VOLUME_KEY = "rtg_admin_audio_booking_rider_volume";

function fire(name, detail) {
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* noop */ }
}

export default function AdminFooter() {
  const [activeLoop, setActiveLoop] = useState(null);

  // v2.7.65+E123 · Per-dispatcher volume + mute preference. Persisted per
  // browser so this laptop's 30 % night-shift setting doesn't blast the
  // next dispatcher.
  const [muted, setMuted] = useState(() => {
    try { return window.localStorage.getItem(MUTE_KEY) === "1"; }
    catch { return false; }
  });
  const [volume, setVolume] = useState(() => {
    try {
      const raw = window.localStorage.getItem(VOLUME_KEY);
      const n = raw === null ? 0.85 : Number(raw);
      return Number.isNaN(n) ? 0.85 : Math.max(0, Math.min(1, n));
    } catch { return 0.85; }
  });
  // v2.7.65+E124 · Independent booking-klaxon slider. Defaults to 1.0 so
  // the historical "you WILL hear a booking" behaviour stays intact.
  const [bookingVolume, setBookingVolume] = useState(() => {
    try {
      const raw = window.localStorage.getItem(BOOKING_VOLUME_KEY);
      const n = raw === null ? 1.0 : Number(raw);
      return Number.isNaN(n) ? 1.0 : Math.max(0, Math.min(1, n));
    } catch { return 1.0; }
  });
  // v2.8+E286 · Independent RIDER-APP booking klaxon slider — lets dispatch
  // make self-service rider bookings louder (or quieter) than call-ins.
  const [bookingRiderVolume, setBookingRiderVolume] = useState(() => {
    try {
      const raw = window.localStorage.getItem(BOOKING_RIDER_VOLUME_KEY);
      const n = raw === null ? 1.0 : Number(raw);
      return Number.isNaN(n) ? 1.0 : Math.max(0, Math.min(1, n));
    } catch { return 1.0; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(VOLUME_KEY, String(volume)); }
    catch { /* quota */ }
    fire("rtg:audio_volume_changed", { volume });
  }, [volume]);
  useEffect(() => {
    try { window.localStorage.setItem(BOOKING_VOLUME_KEY, String(bookingVolume)); }
    catch { /* quota */ }
    fire("rtg:audio_booking_volume_changed", { volume: bookingVolume });
  }, [bookingVolume]);
  useEffect(() => {
    try { window.localStorage.setItem(BOOKING_RIDER_VOLUME_KEY, String(bookingRiderVolume)); }
    catch { /* quota */ }
    fire("rtg:audio_booking_rider_volume_changed", { volume: bookingRiderVolume });
  }, [bookingRiderVolume]);
  useEffect(() => {
    try { window.localStorage.setItem(MUTE_KEY, muted ? "1" : "0"); }
    catch { /* quota */ }
    fire("rtg:audio_mute_changed", { muted });
  }, [muted]);

  const trigger = useCallback((t) => {
    if (t.loop) {
      // Second press of the same loop button silences it.
      if (activeLoop === t.key) {
        fire(t.ackEvent || "rtg:acknowledge_all_alarms", {});
        setActiveLoop(null);
        return;
      }
      fire(t.event, { trip_id: `preview-${Date.now()}`, source: "footer_preview" });
      setActiveLoop(t.key);
    } else {
      fire(t.event, { source: "footer_preview" });
    }
  }, [activeLoop]);

  const stopAll = useCallback(() => {
    fire("rtg:acknowledge_all_alarms", {});
    setActiveLoop(null);
  }, []);

  const VolIcon = muted || volume === 0 ? VolumeX : (volume < 0.5 ? Volume1 : Volume2);
  const volPct  = Math.round(volume * 100);
  const bookingPct = Math.round(bookingVolume * 100);
  const bookingRiderPct = Math.round(bookingRiderVolume * 100);

  return (
    <footer
      className="w-full border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 mt-auto py-4"
      style={{ paddingLeft: 16, paddingRight: 16 }}
      data-testid="admin-compliance-footer"
    >
      {/* ── Sound Preview Strip ────────────────────────────────────── */}
      <div
        className="mx-auto flex flex-wrap items-center justify-center gap-2 mb-3"
        data-testid="footer-sound-preview"
      >
        <span
          className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500 mr-2 flex items-center gap-1.5"
          data-testid="footer-sound-preview-label"
        >
          <Volume2 className="h-3.5 w-3.5"/>Pre-shift sound check
        </span>
        {TESTS.map((t) => {
          const looping = activeLoop === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => trigger(t)}
              data-testid={`footer-sound-test-${t.key}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors font-medium ${
                looping
                  ? "border-rose-400/60 bg-rose-500/10 text-rose-600 dark:text-rose-300 hover:bg-rose-500/20"
                  : "border-slate-300 dark:border-zinc-700 text-slate-700 dark:text-zinc-300 hover:border-amber-400 hover:text-amber-600 dark:hover:text-amber-300"
              }`}
              title={t.loop ? "Play (click again to stop)" : "Play once"}
            >
              {t.label}{looping ? " · click to stop" : ""}
            </button>
          );
        })}
        {activeLoop && (
          <button
            type="button"
            onClick={stopAll}
            data-testid="footer-sound-test-stop-all"
            className="text-[11px] px-2.5 py-1 rounded-full border border-slate-400 dark:border-zinc-600 bg-slate-100 dark:bg-zinc-900 text-slate-700 dark:text-zinc-200 hover:bg-slate-200 dark:hover:bg-zinc-800 flex items-center gap-1"
            title="Silence every active alarm"
          >
            <VolumeX className="h-3 w-3"/>Silence
          </button>
        )}

        {/* v2.7.65+E123 · Personal alarm volume — click the icon to mute,
            drag the slider to set your level. Stored per browser so
            night-shift ops aren't blasted at 100%. */}
        <div
          className="ml-2 pl-3 border-l border-slate-300 dark:border-zinc-700 flex flex-wrap items-center justify-center gap-2"
          data-testid="footer-volume-control"
        >
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            data-testid="footer-volume-mute"
            className={`h-6 w-6 grid place-items-center rounded transition-colors ${
              muted
                ? "text-rose-500 hover:bg-rose-500/10"
                : "text-slate-600 dark:text-zinc-300 hover:text-amber-500 hover:bg-amber-500/10"
            }`}
            title={muted ? "Unmute alarms" : "Mute alarms"}
            aria-label={muted ? "Unmute alarms" : "Mute alarms"}
            aria-pressed={muted}
          >
            <VolIcon className="h-3.5 w-3.5"/>
          </button>
          <div className="flex items-center gap-1.5">
            <span
              className="text-[9px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500 select-none"
              title="Master volume for every alarm except the booking klaxons (Call-In + Rider App)"
            >
              Chimes
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={muted ? 0 : volPct}
              onChange={(e) => {
                const v = Number(e.target.value) / 100;
                setVolume(v);
                if (v > 0 && muted) setMuted(false);
              }}
              data-testid="footer-volume-slider"
              aria-label="Alarm volume (chimes)"
              className="w-20 sm:w-24 h-1 rounded-full appearance-none bg-slate-200 dark:bg-zinc-700 accent-amber-500 disabled:opacity-50"
              disabled={muted}
            />
            <span
              className={`text-[10px] font-mono tabular-nums w-10 text-right ${
                muted ? "text-rose-500 font-bold" : "text-slate-600 dark:text-zinc-400"
              }`}
              data-testid="footer-volume-label"
            >
              {muted ? "MUTED" : `${volPct}%`}
            </span>
          </div>

          {/* v2.7.65+E124 · Independent DISPATCH / CALL-IN booking klaxon
              volume — ops can keep chat chimes quiet while the incoming-booking
              alarm still commands attention. Rose accent to signal urgency. */}
          <div className="flex items-center gap-1.5 pl-2 border-l border-slate-300 dark:border-zinc-700">
            <span
              className="text-[9px] uppercase tracking-widest font-bold text-rose-600 dark:text-rose-400 select-none"
              title="Only affects the dispatch / call-in booking klaxon loop"
            >
              Call-In
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={muted ? 0 : bookingPct}
              onChange={(e) => {
                const v = Number(e.target.value) / 100;
                setBookingVolume(v);
                if (v > 0 && muted) setMuted(false);
              }}
              data-testid="footer-booking-volume-slider"
              aria-label="Dispatch / call-in booking klaxon volume"
              className="w-20 sm:w-24 h-1 rounded-full appearance-none bg-slate-200 dark:bg-zinc-700 accent-rose-500 disabled:opacity-50"
              disabled={muted}
            />
            <span
              className={`text-[10px] font-mono tabular-nums w-10 text-right ${
                muted ? "text-rose-500 font-bold"
                      : bookingVolume >= 0.9 ? "text-rose-600 dark:text-rose-400 font-bold"
                      : "text-slate-600 dark:text-zinc-400"
              }`}
              data-testid="footer-booking-volume-label"
            >
              {muted ? "MUTED" : `${bookingPct}%`}
            </span>
          </div>

          {/* v2.8+E286 · Independent RIDER-APP booking klaxon volume — set the
              self-service rider-app booking alarm louder (or quieter) than
              call-ins so dispatch can tell them apart by ear. Sky accent to
              match the "Rider App" badge used across the console. */}
          <div className="flex items-center gap-1.5 pl-2 border-l border-slate-300 dark:border-zinc-700">
            <span
              className="text-[9px] uppercase tracking-widest font-bold text-sky-600 dark:text-sky-400 select-none"
              title="Only affects the Rider App booking klaxon loop"
            >
              Rider App
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={muted ? 0 : bookingRiderPct}
              onChange={(e) => {
                const v = Number(e.target.value) / 100;
                setBookingRiderVolume(v);
                if (v > 0 && muted) setMuted(false);
              }}
              data-testid="footer-booking-rider-volume-slider"
              aria-label="Rider App booking klaxon volume"
              className="w-20 sm:w-24 h-1 rounded-full appearance-none bg-slate-200 dark:bg-zinc-700 accent-sky-500 disabled:opacity-50"
              disabled={muted}
            />
            <span
              className={`text-[10px] font-mono tabular-nums w-10 text-right ${
                muted ? "text-rose-500 font-bold"
                      : bookingRiderVolume >= 0.9 ? "text-sky-600 dark:text-sky-400 font-bold"
                      : "text-slate-600 dark:text-zinc-400"
              }`}
              data-testid="footer-booking-rider-volume-label"
            >
              {muted ? "MUTED" : `${bookingRiderPct}%`}
            </span>
          </div>
        </div>
      </div>

      {/* ── Legal (centered · flush top-to-bottom, zero-gap block) ── */}
      <div
        className="mx-auto text-center"
        style={{ maxWidth: "56rem" }}
        data-testid="footer-legal-block"
      >
        <p
          className="text-xs sm:text-sm text-slate-700 dark:text-slate-300"
          style={{ margin: 0, padding: 0, lineHeight: 1.2, textAlign: "center" }}
          data-testid="footer-copyright"
        >
          Copyright © 2026 Buller Taxi services, LLC®. | Bison Software, Inc™. All rights reserved.
        </p>
        <p
          className="text-xs sm:text-sm text-slate-600 dark:text-slate-400"
          style={{ margin: 0, padding: 0, lineHeight: 1.2, textAlign: "center" }}
          data-testid="footer-ownership"
        >
          This platform is property of Buller Taxi Services LLC-LP General Partnership Company.
        </p>
      </div>
    </footer>
  );
}
